// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Grouping near-identical frames, and choosing which one to keep.
 *
 * THE ONE INVARIANT THIS FILE EXISTS TO HOLD: every image the user supplied appears exactly once
 * in the decision list. Nothing is ever silently dropped — an image is either kept, or it is
 * listed with a named reason and the file it was judged against. `tests/grouping.test.ts` asserts
 * that the kept set and the dropped set partition the input, because "where did my photo go" is
 * the failure this tool must never have.
 */

import type { Decision, Group, ImageRecord, Thresholds } from './types.ts';
import { hamming } from './image.ts';

/**
 * Weighted union-find with path compression.
 *
 * SINGLE LINKAGE IS A REAL LIMITATION AND IS STATED IN THE INTERFACE, not hidden here. Union-find
 * is transitive: if A is near B and B is near C, then A, B and C form one group even when A and C
 * are not near each other at all. On a long burst that pans slowly across a scene this chains, and
 * the first and last frames of the resulting group can look quite different.
 *
 * Loraprep keeps it anyway, for two reasons. Complete-linkage clustering would need the full pair
 * matrix and a merge policy with its own arbitrary parameter, and — more to the point — the user
 * reviews every group before exporting, with a slider that re-runs this in milliseconds. A visible
 * grouping the user can tighten beats an invisible one that is cleverer.
 */
export class UnionFind {
  private readonly parent: Int32Array;
  private readonly rank: Uint8Array;

  constructor(n: number) {
    this.parent = new Int32Array(n);
    this.rank = new Uint8Array(n);
    for (let i = 0; i < n; i++) this.parent[i] = i;
  }

  find(x: number): number {
    let root = x;
    while (this.parent[root] !== root) root = this.parent[root]!;
    // Path compression, iterative — a 2,000-deep recursion would overflow the worker stack.
    while (this.parent[x] !== root) {
      const next = this.parent[x]!;
      this.parent[x] = root;
      x = next;
    }
    return root;
  }

  union(a: number, b: number): boolean {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra === rb) return false;
    if (this.rank[ra]! < this.rank[rb]!) this.parent[ra] = rb;
    else if (this.rank[ra]! > this.rank[rb]!) this.parent[rb] = ra;
    else {
      this.parent[rb] = ra;
      this.rank[ra]!++;
    }
    return true;
  }
}

export interface Link {
  readonly a: number;
  readonly b: number;
  readonly via: 'hash' | 'embedding';
  /** Hamming distance for a hash link, cosine similarity for an embedding link. */
  readonly score: number;
}

/**
 * All pairs within `maxHamming`.
 *
 * Brute force on purpose. At the 2,000-image ceiling this is 2.0 million 64-bit popcounts, which
 * is a few tens of milliseconds — an LSH index would save nothing measurable and could miss a pair
 * the user can see is a duplicate, which is a far worse trade in a tool whose output is trust.
 */
export function hashLinks(records: readonly ImageRecord[], maxHamming: number): Link[] {
  const links: Link[] = [];
  for (let i = 0; i < records.length; i++) {
    for (let j = i + 1; j < records.length; j++) {
      const d = hamming(records[i]!.hash, records[j]!.hash);
      if (d <= maxHamming) links.push({ a: records[i]!.id, b: records[j]!.id, via: 'hash', score: d });
    }
  }
  return links;
}

/**
 * A frame is a candidate keeper if its sharpness is at least this fraction of its group's best.
 *
 * Measured on the bundled sample, which contains deliberate motion-blurred and downscaled copies
 * alongside their sharp originals. Genuinely blurred frames land at 6-17% of their group's best
 * (5.1/73, 5.1/85, 6.2/71, 11/63, 3.3/48). The spread caused purely by JPEG quality between three
 * copies of the SAME sharp frame is 55-100%. A cut at 35% therefore sits in a wide empty gap —
 * it is a measurement, not a tuned constant.
 */
export const FOCUS_FRACTION = 0.35;

/**
 * How compressed a frame is, as a coarse power-of-two bucket.
 *
 * The bucket, rather than the raw ratio, is what makes this usable: two frames off the same camera
 * at the same quality land in the same bucket and are then decided on sharpness, which is what you
 * want for a real burst. A frame re-saved at a much lower quality drops a whole bucket and loses,
 * which is what you want for a re-encoded copy.
 */
function compressionBucket(m: ImageRecord): number {
  const pixels = m.width * m.height;
  if (pixels <= 0 || m.bytes <= 0) return 0;
  return Math.floor(Math.log2(m.bytes / pixels));
}

/**
 * Pick the frame to keep from a group.
 *
 * SHARPNESS ALONE IS WRONG HERE, and the sample proves it. Variance of the Laplacian measures
 * high-frequency energy, and JPEG blocking IS high-frequency energy — so across three copies of
 * one photograph saved at quality 92, 82 and 76, the measure rises monotonically as quality falls
 * (40 -> 53 -> 73 on the bundled harbour frames) and picks the most damaged file every time. That
 * is the exact opposite of what a training set wants. A 3x3 pre-filter before the Laplacian was
 * tried and does not fix it: blocking is structural, not noise, and survives smoothing (the same
 * three frames go 3.44 -> 5.94 -> 7.24).
 *
 * So the choice uses both signals, each where it is trustworthy:
 *
 *   1. IN FOCUS — sharpness relative to the group's best. This is the one thing the Laplacian
 *      genuinely settles, and it separates a smeared or downscaled frame by a factor of ten.
 *   2. LEAST COMPRESSED — bytes per pixel, bucketed by powers of two. Among frames that are all
 *      in focus, the biggest file is the best training image, full stop.
 *   3. Then sharpness, pixel count, byte size and finally path, so the result is a deterministic
 *      function of the input and a rerun never quietly swaps the keeper.
 *
 * The compression tier is skipped entirely when any member's dimensions are unknown — a header
 * this parser could not read must not be allowed to look like an aggressively compressed one.
 */
export function pickKeeper(members: readonly ImageRecord[]): ImageRecord {
  if (members.length === 1) return members[0]!;

  const bestSharpness = members.reduce((n, m) => Math.max(n, m.sharpness), 0);
  const floor = bestSharpness * FOCUS_FRACTION;
  const useCompression = members.every((m) => m.width > 0 && m.height > 0 && m.bytes > 0);

  const rank = (m: ImageRecord): number[] => [
    m.sharpness >= floor ? 1 : 0,
    useCompression ? compressionBucket(m) : 0,
    m.sharpness,
    m.width * m.height,
    m.bytes,
  ];

  let best = members[0]!;
  let bestRank = rank(best);
  for (const m of members.slice(1)) {
    const r = rank(m);
    let better: boolean | null = null;
    for (let i = 0; i < r.length; i++) {
      if (r[i]! !== bestRank[i]!) {
        better = r[i]! > bestRank[i]!;
        break;
      }
    }
    if (better === null) better = m.path < best.path;
    if (better) {
      best = m;
      bestRank = r;
    }
  }
  return best;
}

export interface GroupingResult {
  readonly groups: readonly Group[];
  readonly decisions: readonly Decision[];
  readonly kept: readonly number[];
}

/**
 * Turn links into groups, then groups into one decision per image.
 *
 * The order of the three drop reasons is deliberate:
 *   1. `excluded`  — a flat, UI-looking frame. Judged on its own, so it applies even to a keeper.
 *   2. `duplicate` — not the keeper of its group.
 *   3. `blurred`   — the keeper of its group, but too soft in absolute terms to be worth training
 *                    on. Only ever applied to a SINGLE-member group, because inside a real group
 *                    "blurred" is already expressed by not being the keeper.
 */
export function buildGroups(
  records: readonly ImageRecord[],
  links: readonly Link[],
  thresholds: Thresholds,
): GroupingResult {
  const byId = new Map<number, ImageRecord>();
  for (const r of records) byId.set(r.id, r);

  const index = new Map<number, number>();
  records.forEach((r, i) => index.set(r.id, i));

  const uf = new UnionFind(records.length);
  const viaByRoot = new Map<number, Set<'hash' | 'embedding'>>();
  for (const l of links) {
    const ia = index.get(l.a);
    const ib = index.get(l.b);
    if (ia === undefined || ib === undefined) continue;
    uf.union(ia, ib);
  }
  // The `via` tally has to be a second pass: a link's root is only final once every union is done.
  for (const l of links) {
    const ia = index.get(l.a);
    if (ia === undefined) continue;
    const root = uf.find(ia);
    let s = viaByRoot.get(root);
    if (!s) viaByRoot.set(root, (s = new Set()));
    s.add(l.via);
  }

  const buckets = new Map<number, number[]>();
  records.forEach((r, i) => {
    const root = uf.find(i);
    const b = buckets.get(root);
    if (b) b.push(r.id);
    else buckets.set(root, [r.id]);
  });

  // Deterministic group ordering: by the smallest member id.
  const roots = [...buckets.keys()].sort((x, y) => buckets.get(x)![0]! - buckets.get(y)![0]!);

  const groups: Group[] = [];
  const decisions: Decision[] = [];
  const kept: number[] = [];

  roots.forEach((root, gi) => {
    const memberIds = buckets.get(root)!.slice().sort((a, b) => a - b);
    const members = memberIds.map((id) => byId.get(id)!);
    const keeper = pickKeeper(members);
    const viaSet = viaByRoot.get(root);
    const via: Group['via'] =
      !viaSet || viaSet.size === 0
        ? 'hash'
        : viaSet.size === 2
          ? 'both'
          : viaSet.has('embedding')
            ? 'embedding'
            : 'hash';

    groups.push({ id: gi, members: memberIds, keeper: keeper.id, via });

    for (const m of members) {
      if (thresholds.excludeFlat && m.flatness >= FLAT_THRESHOLD) {
        decisions.push({
          id: m.id,
          verdict: 'excluded',
          reason: `${(m.flatness * 100).toFixed(0)}% of pixels are one of six colours — looks like a screenshot or a flat graphic, not a photograph`,
          against: null,
          groupId: gi,
        });
        continue;
      }
      if (m.id !== keeper.id) {
        const d = hamming(m.hash, keeper.hash);
        decisions.push({
          id: m.id,
          verdict: 'duplicate',
          reason:
            `near-duplicate of ${keeper.path} (hamming ${d}, sharpness ` +
            `${m.sharpness.toFixed(1)} vs ${keeper.sharpness.toFixed(1)})`,
          against: keeper.id,
          groupId: gi,
        });
        continue;
      }
      // A keeper standing alone can still be too soft to be worth training on. Inside a real
      // group this test is redundant — being the keeper already means being the sharpest.
      if (members.length === 1 && m.sharpness < SOLO_BLUR_FLOOR && thresholds.blurRatio > 0) {
        decisions.push({
          id: m.id,
          verdict: 'blurred',
          reason: `no sharp detail anywhere in the frame (Laplacian variance ${m.sharpness.toFixed(1)})`,
          against: null,
          groupId: gi,
        });
        continue;
      }
      decisions.push({ id: m.id, verdict: 'kept', reason: 'kept', against: null, groupId: gi });
      kept.push(m.id);
    }
  });

  decisions.sort((a, b) => a.id - b.id);
  kept.sort((a, b) => a - b);
  return { groups, decisions, kept };
}

/**
 * A frame with 55% of its pixels in six luminance buckets. Measured on the bundled sample: the
 * three synthetic screenshots land at 0.62-0.81 and no photographic frame exceeds 0.21. It is
 * still a heuristic on somebody else's photographs, which is why the switch defaults to off.
 */
export const FLAT_THRESHOLD = 0.55;

/**
 * The absolute blur floor, applied ONLY to a frame with no group to be compared against.
 *
 * A number this arbitrary would normally be indefensible — see the caveat on
 * `varianceOfLaplacian`. It is set very low deliberately: at 12 on the fixed 256x256 working grid
 * it catches a frame with essentially no edge energy at all (a smeared pan, a lens cap, a
 * completely defocused shot) and leaves every merely-soft image alone. A user who wants a stricter
 * cut does it by looking at the review grid, not by trusting this.
 */
export const SOLO_BLUR_FLOOR = 12;
